import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router';
import requestGetHeroPlannerSchedule from '~/queries/customerCommunication/requestGetHeroPlannerSchedule';
import requestPostCustomSchedule from '~/queries/customerCommunication/requestPostCustomSchedule';
import requestPostHeroPlannerSchedule from '~/queries/customerCommunication/requestPostHeroPlannerSchedule';

import { Alert, AlertTitle, Button, CircularProgress } from '@mui/material';

import { FORM_TYPE_CUSTOM, FORM_TYPE_DEFAULT } from '~/consts/customerCommunication/heroPlanner';
import { RequestStatus } from '~/consts/requestConstants';

import { useAppSelector } from '~/hooks/store';
import useQuery from '~/hooks/useQuery';

import { getDayJs } from '~/services/TimeService';

import { isRequestInitial, isRequestPending, isRequestRejected } from '~/utils/requestUtils';

import { isScheduleIsCustomScheduleType } from '../utils/heroPlannerScheduleUtils';

import ScheduleEntityForm from './ScheduleEntityForm';
import { SCHEDULE_SEARCH_PARAMS } from './SchedulesFilters';

interface Props {
  scheduleId?: string;
  formType: string;
  editorType: 'new' | 'duplicate';
}

const EMPTY_SCHEDULE: CustomerCommunication.UnsavedHeroPlannerSchedule = {
  sendDate: getDayJs().add(1, 'day').tz(window.configs.DEFAULT_TIMEZONE),
  brandId: '',
  countryGroupId: '',
  countryId: '',
  stateId: '',
  cityId: '',
  segmentId: '',
  cadenceId: '',
  membershipId: '',

  isApprovedForTpfm: false,
  emailSubjectLine: '',
  emailPreHeader: '',
  emailBannerId: undefined,
  emailBannerPosition2Id: undefined,
  emailHeroOfferIds: [],
  pushTitle: '',
  pushSubtitle: '',
  pushMessage: '',
  pushLink: '',
  pushSvcImageId: '',
};

type CombinedUnSavedSchedule = CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.CustomSchedule;

function NewScheduleForm({ scheduleId, formType, editorType }: Props) {
  const history = useHistory();
  const brandId = useAppSelector((state) => state.tenant.brand) || '';
  const query = useQuery();

  const initialSchedule = useMemo<CustomerCommunication.UnsavedHeroPlannerSchedule>(() => {
    const schedule: CustomerCommunication.UnsavedHeroPlannerSchedule = {
      ...EMPTY_SCHEDULE,
      brandId,
    };
    if (editorType === 'new') {
      const sendDateParam = query.get(SCHEDULE_SEARCH_PARAMS.FROM_DATE);
      const countryGroupParam = query.get(SCHEDULE_SEARCH_PARAMS.COUNTRY_GROUP);
      const countryParam = query.get(SCHEDULE_SEARCH_PARAMS.COUNTRY);
      const stateParam = query.get(SCHEDULE_SEARCH_PARAMS.COUNTRY_STATE);

      if (sendDateParam) {
        schedule.sendDate = getDayJs(sendDateParam).tz(window.configs.DEFAULT_TIMEZONE);
      }
      if (countryGroupParam) {
        schedule.countryGroupId = countryGroupParam;
      }
      if (countryParam) {
        schedule.countryId = countryParam;
      }
      if (stateParam) {
        schedule.stateId = stateParam;
      }
    }
    return schedule;
  }, [brandId, editorType, query]);

  const [existingScheduleReq, setExistingScheduleReq] = useState<Utils.RequestState<CombinedUnSavedSchedule, string>>({
    status: RequestStatus.INITIAL,
  });
  const requestExistingSchedule = useCallback(async () => {
    setExistingScheduleReq({
      status: RequestStatus.PENDING,
      params: scheduleId,
    });

    try {
      const result = await requestGetHeroPlannerSchedule(scheduleId, formType);
      const { id, ...unsaved } = result;
      setExistingScheduleReq({
        status: RequestStatus.FULFILLED,
        params: scheduleId,
        // TODO: Remove the type assertion
        result: unsaved as CombinedUnSavedSchedule,
      });
    } catch (error) {
      setExistingScheduleReq({
        status: RequestStatus.REJECTED,
        params: scheduleId,
        error,
      });
    }
  }, [scheduleId, formType]);

  useEffect(() => {
    if (scheduleId) {
      requestExistingSchedule();
    }
  }, [requestExistingSchedule, scheduleId]);

  const [submissionReq, setSubmissionReq] = useState<
    Utils.RequestState<
      CustomerCommunication.HeroPlannerSchedule | CustomerCommunication.CustomSchedulePostPayload,
      string,
      CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.CustomSchedule
    >
  >({
    status: RequestStatus.INITIAL,
  });
  const handleSubmission = useCallback(
    async (unsaved: CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.CustomSchedule) => {
      setSubmissionReq({
        status: RequestStatus.PENDING,
        params: unsaved,
      });

      try {
        const isCustomSchedule = isScheduleIsCustomScheduleType(unsaved);
        const computedFormType = isCustomSchedule ? FORM_TYPE_CUSTOM : FORM_TYPE_DEFAULT;
        if (isCustomSchedule) {
          const createdCustomSchedule = await requestPostCustomSchedule(unsaved);
          setSubmissionReq({
            status: RequestStatus.FULFILLED,
            params: unsaved,
            result: createdCustomSchedule,
          });
          history.push(
            `/customer-communication/hero-planner/schedules/${createdCustomSchedule.id}?formType=${computedFormType}`,
          );
        } else {
          const createdHeroPlannerSchedule = await requestPostHeroPlannerSchedule(unsaved);
          setSubmissionReq({
            status: RequestStatus.FULFILLED,
            params: unsaved,
            result: createdHeroPlannerSchedule,
          });
          history.push(`/customer-communication/hero-planner/schedules/${createdHeroPlannerSchedule.id}`);
        }
      } catch (error) {
        setSubmissionReq({
          status: RequestStatus.REJECTED,
          params: unsaved,
          error,
        });
      }
    },
    [history],
  );

  if ((!!scheduleId && isRequestInitial(existingScheduleReq)) || isRequestPending(existingScheduleReq)) {
    return <CircularProgress />;
  }

  if (isRequestRejected(existingScheduleReq)) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" color="inherit" onClick={requestExistingSchedule}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Could not fetch the schedule.</AlertTitle>
        <pre>{existingScheduleReq.error}</pre>
      </Alert>
    );
  }

  return (
    <>
      <ScheduleEntityForm
        editorType={editorType}
        schedule={existingScheduleReq.result ?? initialSchedule}
        busy={isRequestPending(submissionReq)}
        onSubmit={handleSubmission}
      />
      {isRequestRejected(submissionReq) && (
        <Alert severity="error">
          <AlertTitle>Please resolve the following:</AlertTitle>
          <pre>{submissionReq.error}</pre>
        </Alert>
      )}
    </>
  );
}

export default NewScheduleForm;
